import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './integration-card.css';
import { QCard, QCardContent, QTypography, QButton, QTextField, QDropdownWrapper } from '@kw/quokka-ui';
import { styled } from '@mui/material';
import { SettingsMode } from './enum/SettingsMode.enum';
import { httpRequest, sendHttpRequest } from '../../utils/network.service';
import { LaunchDarklyFlags } from '../../launch-darkly';
import { verifyAuthPermission } from '../../auth/VerifyAuthPermission';
import { AuthPermissions } from '../../auth/AuthPermissions';
import { KwFormErrorMessage } from '../../kw-ui-components/KwFormErrorMessage';
import { StyledIntegrationCard } from '../shared/StyledIntegrationCard';

interface IntuneIntegrationCardProps {
  baseUrlPlaceholder: string;
  clientIdPlaceholder: string;
  clientSecretPlaceholder: string;
  tenantIdPlaceholder: string;
  lastSync: string;
  isAdmin: boolean;
  mode: { [key: string]: SettingsMode };
  setMode: React.Dispatch<React.SetStateAction<{ [key: string]: SettingsMode }>>;
  mutate: () => void;
}

export const IntuneIntegrationCard = ({
  baseUrlPlaceholder,
  clientIdPlaceholder,
  clientSecretPlaceholder,
  tenantIdPlaceholder,
  lastSync,
  isAdmin,
  mode,
  setMode,
  mutate,
}: IntuneIntegrationCardProps) => {
  const isEditMode = mode.intune === SettingsMode.EDIT;
  const isReadMode = mode.intune === SettingsMode.READ;
  const [baseUrl, setBaseUrl] = useState('https://graph.microsoft.com');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [intuneFormErrors, setIntuneFormErrors] = useState({
    url: false,
    tenant: false,
    client: false,
    clientSecret: false,
    validated: false,
  });
  const [connectionFailed, setConnectionFailed] = useState(false);
  const lastSyncTimestamp = lastSync ? new Date(parseInt(lastSync, 10)).toLocaleString() : 'Never';

  const resetInputValues = () => {
    setClientId('');
    setClientSecret('');
    setTenantId('');
    setConnectionFailed(false);
    setIntuneFormErrors({ url: false, tenant: false, client: false, clientSecret: false, validated: false });
    setMode({ ...mode, intune: SettingsMode.READ });
  };

  function valdiateIntuneSettings() {
    setIntuneFormErrors({
      url: !baseUrl.length,
      tenant: !tenantId.length,
      client: !clientId.length,
      clientSecret: !clientSecret.length,
      validated: true,
    });
  }

  const updateIntuneSettings = async () => {
    valdiateIntuneSettings();

    if (!tenantId.length || !clientId.length || !clientSecret.length) {
      return;
    }

    const existingSettings = clientIdPlaceholder.length || clientSecretPlaceholder.length || tenantIdPlaceholder.length;
    let path = `${process.env.ORGANIZATION_SERVICE_URL}`;
    path += existingSettings ? '/organizations/mdm-integrations/1' : '/organizations/mdm-integrations';
    const method = existingSettings ? 'PUT' : 'POST';
    const body = existingSettings
      ? {}
      : { mdmIntegrationId: 1, settings: { base_url: baseUrl, client_id: clientId, client_secret: clientSecret, tenant_id: tenantId } };

    if (existingSettings) {
      const settings = {};

      Object.assign(settings, { base_url: baseUrl });

      if (clientId.length) {
        Object.assign(settings, { ...settings, client_id: clientId });
      }
      if (clientSecret.length) {
        Object.assign(settings, { ...settings, client_secret: clientSecret });
      }
      if (tenantId.length) {
        Object.assign(settings, { ...settings, tenant_id: tenantId });
      }

      Object.assign(body, { settings });
    }

    const connectionResult = await httpRequest({
      path: `${process.env.INTUNE_SERVICE_URL}/intune/check-connection`,
      method: 'POST',
      body: {
        baseUrl,
        clientId,
        clientSecret,
        tenantId,
      },
    });

    if (connectionResult.response.status !== 200) {
      setConnectionFailed(true);
      return;
    }

    await sendHttpRequest({
      path,
      method,
      body,
      successMessage: 'Intune settings updated successfully',
      errorMessage: 'Error updating Intune settings',
      enqueueSnackbar,
    });

    await syncNow();

    mutate();
    resetInputValues();
  };

  const syncNow = async () => {
    await sendHttpRequest({
      path: `${process.env.INTUNE_SERVICE_URL}/intune/sync`,
      method: 'POST',
      successMessage: 'Intune sync initiated successfully',
      errorMessage: 'Error initiating Intune sync',
      enqueueSnackbar,
    });
  };

  const flags: LaunchDarklyFlags['flags'] = useFlags();

  if (!flags?.intuneIntegration || !verifyAuthPermission(AuthPermissions.SETTINGS_INTEGRATIONS_MDM)) return null;

  const baseUrlOptions = [
    {
      value: 'https://graph.microsoft.com',
      label: 'Global: https://graph.microsoft.com',
    },
    {
      value: 'https://graph.microsoft.us',
      label: 'US Gov L4: https://graph.microsoft.us',
    },
    {
      value: 'https://dod-graph.microsoft.us',
      label: 'US Gov L5: https://dod-graph.microsoft.us',
    },
  ];

  return (
    <StyledIntegrationCard>
      <QCardContent>
        <StyledQTypography variant="h4Bold" gutterBottom>
          <img
            src="/logo/intune.png" className="logo"
          />
          Microsoft Intune
        </StyledQTypography>
        <KwFormErrorMessage error={Boolean(connectionFailed)} reason="We couldn't make a connection with the information provided" />
        <StyledQTypography variant="bodyRegular" gutterBottom>
          Configure Q-scout&apos;s connection to the Graph API to import Managed and Unmanaged Mobile Apps from your Intune tenant.{' '}
          <a
            href="https://learn.microsoft.com/en-us/graph/api/resources/intune-graph-overview?view=graph-rest-1.0"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </StyledQTypography>
        {isAdmin && isEditMode ? (
          <>
            <StyledQTypography variant="h5Bold">Base URL</StyledQTypography>
            <QDropdownWrapper
              selectedOption={baseUrl}
              closedLabel={baseUrlOptions.find(option => option.value === baseUrl)?.label ?? 'Select Base URL'}
              formControlProps={{ sx: { width: '100%' } }}
              onChange={e => setBaseUrl(e.target.value as string)}
              options={baseUrlOptions}
            />
            <StyledQTypography variant="h5Bold">Directory (tenant) ID</StyledQTypography>
            <QTextField
              placeholder={tenantIdPlaceholder}
              onChange={e => setTenantId(e.target.value)}
              error={Boolean(intuneFormErrors.tenant)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.tenant)} reason="Enter a Directory (tenant) ID" />
            <StyledQTypography variant="h5Bold">Application (client) ID</StyledQTypography>
            <QTextField
              placeholder={clientIdPlaceholder}
              onChange={e => setClientId(e.target.value)}
              error={Boolean(intuneFormErrors.client)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.client)} reason="Enter an Application (client) ID" />
            <StyledQTypography variant="h5Bold">Client Secret Value</StyledQTypography>
            <QTextField
              placeholder={clientSecretPlaceholder}
              onChange={e => setClientSecret(e.target.value)}
              error={Boolean(intuneFormErrors.clientSecret)}
            />
            <KwFormErrorMessage error={Boolean(intuneFormErrors.clientSecret)} reason="Enter a Client Secret Value" />
          </>
        ) : (
          <>
            <StyledQTypography variant="h5Bold">Base URL</StyledQTypography>
            <StyledQTypography>{baseUrlPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Directory (tenant) ID</StyledQTypography>
            <StyledQTypography>{tenantIdPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Application (client) ID</StyledQTypography>
            <StyledQTypography>{clientIdPlaceholder}</StyledQTypography>
            <StyledQTypography variant="h5Bold">Client Secret Value</StyledQTypography>
            <StyledQTypography>{clientSecretPlaceholder}</StyledQTypography>
          </>
        )}

        <div className="action-wrapper">
          {isAdmin && isEditMode ? (
            <>
              <QButton onClick={resetInputValues}>Cancel</QButton>
              <QButton variant="filled" onClick={updateIntuneSettings}>
                Save
              </QButton>
            </>
          ) : (
            <>
              {isAdmin && isReadMode ? (
                <>
                  <div className="sync-action-wrapper">
                    <QButton
                      style={{ padding: 0 }}
                      variant="text"
                      onClick={() => syncNow()}
                      disabled={!clientIdPlaceholder || !clientSecretPlaceholder || !tenantIdPlaceholder}
                    >
                      <img src="icons/sync-now.svg" className="sync-now-icon" />
                      <span className="sync-now-button-text">Sync Now</span>
                    </QButton>
                    <QTypography sx={{ ml: 1, color: '#757575' }}>Last synced: {lastSyncTimestamp}</QTypography>
                  </div>
                  <QButton variant="filled" onClick={() => setMode({ ...mode, intune: SettingsMode.EDIT })}>
                    Edit
                  </QButton>
                </>
              ) : (
                <QButton disabled variant="filled">
                  Save
                </QButton>
              )}
            </>
          )}
        </div>
      </QCardContent>
    </StyledIntegrationCard>
  );
};

const StyledQTypography = styled(QTypography)`
  margin: 20px 0;
  display: block;
`;
